import React from "react";
import { Trash2, Plus } from "lucide-react";
import { ClaimNonOrderProductColumnHeader } from "src/enums";
import { ClaimNonOrderProductInputField, TableColumn } from "src/types";
import { TextInputBox, TextInputBoxLarge } from "src/components/ui/inputs";
import { Table } from "src/components";
import { ClaimMapper } from "src/mappers";

interface EditClaimNonOrderProductInputTableProps {
  nonOrderProducts: ClaimNonOrderProductInputField[];
  setNonOrderProducts: React.Dispatch<React.SetStateAction<ClaimNonOrderProductInputField[]>>;
  minNonOrderProducts?: number;
  getProductError?: (
    productIndex: number,
    fieldName: "productId" | "claimQuantity" | "extraInfo"
  ) => string | undefined;
  generalError?: string;
  // Product details from the claim (read-only)
  productDetails?: Map<string, { productName: string; sku: string; barcode?: string; unitPrice: number }>;
}

const EditClaimNonOrderProductInputTable: React.FC<EditClaimNonOrderProductInputTableProps> = ({
  nonOrderProducts,
  setNonOrderProducts,
  minNonOrderProducts = 0,
  getProductError,
  generalError,
  productDetails
}) => {
  const handleProductInputChange = <T,>(id: string, fieldName: keyof ClaimNonOrderProductInputField, value: T) => {
    setNonOrderProducts(
      nonOrderProducts.map((product) => (product.fieldId === id ? { ...product, [fieldName]: value } : product))
    );
  };

  const handleAddProduct = () => {
    const newProduct = ClaimMapper.initClaimNonOrderProductInputField();
    setNonOrderProducts([...nonOrderProducts, newProduct]);
  };

  const EditClaimNonOrderProductInputTableColumns: TableColumn<
    ClaimNonOrderProductColumnHeader,
    ClaimNonOrderProductInputField
  >[] = [
    {
      header: ClaimNonOrderProductColumnHeader.PRODUCT_ID,
      column: (row: ClaimNonOrderProductInputField) => {
        const productIndex = nonOrderProducts.findIndex((p) => p.fieldId === row.fieldId);
        const error = getProductError ? getProductError(productIndex, "productId") : undefined;
        const productDetail = productDetails?.get(row.productId);

        return (
          <div className="w-48">
            {productDetail ? (
              // Existing product - read-only
              <div className="text-sm text-secondary-700 bg-secondary-50 px-3 py-2 rounded border border-secondary-200 cursor-not-allowed">
                {row.productId}
              </div>
            ) : (
              // New product - editable
              <TextInputBox
                id={`edit-non-order-product-id-${row.fieldId}`}
                value={row.productId ?? ""}
                onChange={(value) => handleProductInputChange(row.fieldId, "productId", value)}
                maxLength={100}
                placeholder="Product ID"
                size="sm"
                error={error}
              />
            )}
          </div>
        );
      },
      styles: { minWidth: "200px" }
    },
    {
      header: "Product Name",
      column: (row: ClaimNonOrderProductInputField) => {
        const productDetail = productDetails?.get(row.productId);
        return (
          <div className="w-full min-w-[200px]">
            <div className="text-sm text-secondary-700 bg-secondary-50 px-3 py-2 rounded border border-secondary-200 cursor-not-allowed">
              {productDetail?.productName || "-"}
            </div>
          </div>
        );
      },
      styles: { minWidth: "200px" }
    },
    {
      header: "SKU",
      column: (row: ClaimNonOrderProductInputField) => {
        const productDetail = productDetails?.get(row.productId);
        return (
          <div className="w-full min-w-[150px]">
            <div className="text-sm text-secondary-700 bg-secondary-50 px-3 py-2 rounded border border-secondary-200 cursor-not-allowed">
              {productDetail?.sku || "-"}
            </div>
          </div>
        );
      },
      styles: { minWidth: "150px" }
    },
    {
      header: "Barcode",
      column: (row: ClaimNonOrderProductInputField) => {
        const productDetail = productDetails?.get(row.productId);
        return (
          <div className="w-full min-w-[150px]">
            <div className="text-sm text-secondary-700 bg-secondary-50 px-3 py-2 rounded border border-secondary-200 cursor-not-allowed">
              {productDetail?.barcode || "-"}
            </div>
          </div>
        );
      },
      styles: { minWidth: "150px" }
    },
    {
      header: "Unit Price",
      column: (row: ClaimNonOrderProductInputField) => {
        const productDetail = productDetails?.get(row.productId);
        return (
          <div className="w-full min-w-[120px]">
            <div className="text-sm text-secondary-700 bg-secondary-50 px-3 py-2 rounded border border-secondary-200 cursor-not-allowed">
              {productDetail?.unitPrice ? `£${productDetail.unitPrice.toFixed(2)}` : "-"}
            </div>
          </div>
        );
      },
      styles: { minWidth: "120px" }
    },
    {
      header: ClaimNonOrderProductColumnHeader.CLAIM_QUANTITY,
      column: (row: ClaimNonOrderProductInputField) => {
        const claimQuantity =
          nonOrderProducts.find((product) => product.fieldId === row.fieldId)?.claimQuantity?.toString() || "";
        const productIndex = nonOrderProducts.findIndex((p) => p.fieldId === row.fieldId);
        const error = getProductError ? getProductError(productIndex, "claimQuantity") : undefined;

        return (
          <div className="w-24">
            <TextInputBox
              id={`edit-non-order-claim-quantity-${row.fieldId}`}
              value={claimQuantity}
              onChange={(value) => handleProductInputChange(row.fieldId, "claimQuantity", value)}
              maxLength={10}
              placeholder="Qty"
              size="sm"
              error={error}
            />
          </div>
        );
      },
      styles: { minWidth: "120px" }
    },
    {
      header: ClaimNonOrderProductColumnHeader.EXTRA_INFO,
      column: (row: ClaimNonOrderProductInputField) => {
        const extraInfo = nonOrderProducts.find((product) => product.fieldId === row.fieldId)?.extraInfo || "";

        return (
          <div className="w-full min-w-[200px]">
            <TextInputBoxLarge
              id={`edit-non-order-extra-info-${row.fieldId}`}
              value={extraInfo}
              onChange={(value) => handleProductInputChange(row.fieldId, "extraInfo", value)}
              maxLength={1000}
              placeholder="Extra information (optional)"
              rows={2}
            />
          </div>
        );
      },
      styles: { minWidth: "250px" }
    },
    {
      header: ClaimNonOrderProductColumnHeader.PLACE_HOLDER,
      column: (row: ClaimNonOrderProductInputField) => (
        <button
          onClick={() => {
            if (nonOrderProducts.length > minNonOrderProducts) {
              setNonOrderProducts(nonOrderProducts.filter((product) => product.fieldId !== row.fieldId));
            }
          }}
          disabled={nonOrderProducts.length <= minNonOrderProducts}
          className="group flex items-center justify-center p-2 rounded-md hover:bg-error-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          title="Delete product"
          type="button"
        >
          <Trash2 className="w-4 h-4 text-error-600 group-hover:text-error-700 transition-colors" />
        </button>
      ),
      styles: { minWidth: "60px", textAlign: "center" }
    }
  ];

  return (
    <div>
      {generalError && (
        <div className="mb-3 p-3 bg-error-50 border border-error-200 rounded-lg">
          <p className="text-sm text-error-700">{generalError}</p>
        </div>
      )}
      <Table columns={EditClaimNonOrderProductInputTableColumns} data={nonOrderProducts} />
      <div className="mt-3">
        <button
          type="button"
          onClick={handleAddProduct}
          className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Non-Order Product
        </button>
      </div>
    </div>
  );
};

export { EditClaimNonOrderProductInputTable };
